import { useCallback, useEffect, useState } from 'react';
import {
    fetchFeatureFlagsFromDb,
    updateFeatureFlagInDb,
} from '../services/featureFlagsService';
import { getErrorMessage } from '../utils/errors';
import type { FeatureFlag } from '../types';

export function useFeatureFlags() {
    const [flags, setFlags] = useState<FeatureFlag[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [savingId, setSavingId] = useState<string | null>(null);

    const fetchFlags = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchFeatureFlagsFromDb();
            setFlags(data);
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to load feature flags'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFlags();
    }, [fetchFlags]);

    const toggleFlag = useCallback(
        async (id: string) => {
            const current = flags.find((f) => f.id === id);
            if (!current) return;

            const nextEnabled = !current.enabled;

            // Optimistic update
            setFlags((prev) =>
                prev.map((f) => (f.id === id ? { ...f, enabled: nextEnabled } : f))
            );
            setSavingId(id);
            setError(null);

            try {
                const updated = await updateFeatureFlagInDb(id, nextEnabled);
                setFlags((prev) => prev.map((f) => (f.id === id ? updated : f)));
            } catch (err) {
                // Revert on failure
                setFlags((prev) =>
                    prev.map((f) =>
                        f.id === id ? { ...f, enabled: current.enabled } : f
                    )
                );
                setError(getErrorMessage(err, 'Failed to update feature flag'));
            } finally {
                setSavingId(null);
            }
        },
        [flags]
    );

    return { flags, loading, error, savingId, toggleFlag, refetch: fetchFlags };
}